import { forwardRef, useEffect, useState } from "react";
import * as React from "react";
import Button, { ButtonStyle } from "@ebsco-ui/button";
import { createBemBlockBuilder } from "@ebsco-ui/utils";
import IndeterminateSpinner from "@ebsco-ui/indeterminate-spinner";
import cn from "classnames";

import "@ebsco-ui/button/button.scss";
import "./loading-button.scss";

export type ExtendedButtonStyle = ButtonStyle | "standard";
export type ButtonType = "button" | "submit" | "reset";

export interface LoadingButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  announceLoadingStart?: string;
  announceLoadingEnd?: string;
  loadingPlaceholder?: string;
  isLoading?: boolean;
  className?: string;
  styleType?: ExtendedButtonStyle;
  type?: ButtonType;
}

const extendedStyleType = ["standard"];

const getStyleType = (styleType: ExtendedButtonStyle): ButtonStyle =>
  extendedStyleType.includes(styleType)
    ? "default"
    : (styleType as ButtonStyle);

const baseClassName = "eb-loading-button";

const LoadingButton = forwardRef<HTMLButtonElement, LoadingButtonProps>(
  (
    {
      isLoading = false,
      disabled = false,
      styleType = "default",
      type = "button",
      announceLoadingStart,
      announceLoadingEnd,
      loadingPlaceholder,
      className,
      children,
      ...rest
    }: LoadingButtonProps,
    forwardedRef,
  ) => {
    const getBlocksWith = createBemBlockBuilder([baseClassName, className]);

    // This is required to prevent announcement
    // of the loading state on initial component render
    const [shouldAnnounce, setShouldAnnounce] = useState(false);

    useEffect(() => {
      isLoading && setShouldAnnounce(true);
    }, [isLoading]);

    return (
      <>
        {shouldAnnounce && (
          <div role="alert" className="screen-reader-text" aria-live="polite">
            {isLoading ? announceLoadingStart : announceLoadingEnd}
          </div>
        )}

        <Button
          ref={forwardedRef}
          className={cn(
            getBlocksWith(),
            getBlocksWith(`--${styleType}`),
            loadingPlaceholder && isLoading && getBlocksWith("--inline"),
            disabled && getBlocksWith("--disabled"),
            isLoading && getBlocksWith("--loading"),
          )}
          data-auto={baseClassName}
          disabled={disabled || isLoading}
          styleType={getStyleType(styleType)}
          type={type}
          {...rest}
        >
          {children && (
            <div
              data-auto={`${baseClassName}-content`}
              className={cn(
                getBlocksWith("__content"),
                isLoading && getBlocksWith("__content--hidden"),
              )}
            >
              {children}
            </div>
          )}
          {isLoading && (
            <div
              data-auto={`${baseClassName}-loader`}
              className={getBlocksWith("__loader")}
            >
              {loadingPlaceholder}
              <IndeterminateSpinner
                className={getBlocksWith("__spinner")}
                level="component"
                aria-hidden="true"
              />
            </div>
          )}
        </Button>
      </>
    );
  },
);

export default LoadingButton;
